import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useHistory, useParams } from "react-router-dom"
import { DetailsLayout } from "../components/bifrost"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import {
	Grid,
	Box,
	Tooltip,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	Typography,
	IconButton,
	Popover,
} from "@material-ui/core"
import LabelValue from "../components/bifrost/common/LabelValue"
import { getBifrostJobDetailsRequest, getExtractJobDetailsRequest } from "../actions/bifrostActions"
import { getFormattedTime, getStatusColor } from "../utils/helperFunctions"
import TableView from "../components/common/TableView/TableView"
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline"
import { getFileFieldValues } from "../services/bifrostServices"
import { useTranslation } from "react-i18next"
import WithDrawer from "../components/common/drawer/withDrawer"
const useStyles = makeStyles((theme) => ({
	jobDetails: {
		"& .MuiChip-root": {
			borderRadius: "4px",
		},
		"& .MuiDataGrid-cell": {
			padding: "0 16px",
		},
	},
	actionDiv: {
		display: "flex",
		justifyContent: "space-between",
	},
	primaryBtn: {
		textTransform: "initial",
		fontSize: theme.typography.fontSize,
		padding: "8px 12px",
		color: theme.palette.primary.dark,
	},
	formControl: {
		margin: theme.spacing(1),
		width: 140,
		"& .MuiSelect-icon": {
			color: theme.palette.common.black,
		},
		"& .MuiSelect-outlined": {
			backgroundColor: theme.palette.grey[100],
			borderRadius: "5px",
		},
		"& .MuiOutlinedInput-notchedOutline": {
			border: "transparent",
		},
	},
	tracebackPopover: {
		width: "380px",
		height: "222px",
		padding: "20px",
		"& .MuiPopover-paper": {
			border: `2px solid ${theme.palette.status.failed_border}`,
			borderLeftWidth: "4px",
			boxShadow: theme.shadows[27],
			borderRadius: "3px",
			padding: "8px",
		},
	},
	popoverContent: {
		fontSize: "12px",
	},
	errorIcon: {
		color: theme.palette.error.main,
	},
	errorMessage: {
		width: "340px",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		overflow: "hidden",
		color: theme.palette.grey.D100,
	},
	summarTableTitle: {
		padding: 20,
	},
}))
const PageGeneratedSummaryDetails = () => {
	const dispatch = useDispatch()
	const [t] = useTranslation("global")
	const classes = useStyles()
	const history = useHistory()
	const { caseId, jobId } = useParams()
	const theme = useTheme()

	const bifrostJobDetails = useSelector((state) => state?.bifrostExtractDetails?.bifrostJobDetails)
	const isPageLoading = useSelector((state) => state?.bifrostExtractDetails?.isLoading)

	const [pageNumber, setPageNumber] = useState(1)
	const [pageSize, setPageSize] = useState(10)
	const [fileStatus, setFileStatus] = useState("")
	const [fileType, setFileType] = useState("")
	const [fileStatusList, setFileStatusList] = useState([])
	const [fileTypesList, setFileTypeList] = useState([])
	const [anchorEl, setAnchorEl] = useState(null)
	const [popoverContent, setPopoverContent] = useState(null)

	const MenuProps = {
		PaperProps: {
			style: {
				maxWidth: 216,
				maxHeight: 300,
			},
		},
		anchorOrigin: {
			vertical: "bottom",
			horizontal: "left",
		},
		getContentAnchorEl: null,
	}

	const showTraceBox = Boolean(anchorEl)

	useEffect(() => {
		getBifrostJobDetails()
		getFileStatusList()
		getFileTypeList()
	}, [])

	useEffect(() => {
		getBifrostJobDetails()
	}, [pageNumber, pageSize])

	useEffect(() => {
		getBifrostJobDetails(true)
	}, [fileStatus, fileType])

	const getBifrostJobDetails = (resetPage) => {
		const payload = {
			jobId: jobId,
			filters: {
				file_status: fileStatus,
				file_type: fileType,
			},
			page_number: resetPage ? 1 : pageNumber,
			page_size: pageSize,
		}
		dispatch(getBifrostJobDetailsRequest(payload))
	}

	const getFileStatusList = async () => {
		const result = await getFileFieldValues(jobId, caseId, "jobs.file_status")
		setFileStatusList(result.data.unique_values)
	}

	const getFileTypeList = async () => {
		const result = await getFileFieldValues(jobId, caseId, "original_file_type")
		setFileTypeList(result.data.unique_values)
	}

	const getActionItems = () => {
		return (
			<div className={classes.actionDiv}>
				<Button
					onClick={() => {
						history.push(`/bifrost/case/${caseId}`)
					}}
					className={classes.primaryBtn}
				>
					{t("bifrost.buttonTexts.back")}
				</Button>
			</div>
		)
	}

	const getRows = () => {
		return bifrostJobDetails?.files
			? bifrostJobDetails.files.map((file) => ({
					id: file.file_id,
					name: file.name,
					original_file_type: file.original_file_type,
					size: file.human_readable_size || "NA",
					path: file.original_file?.path,
					file_status: file.jobs[0]?.file_status,
					exception: file.jobs[0]?.exception,
			  }))
			: []
	}

	const getToolTipSection = (value) => {
		return (
			<Tooltip title={value} placement="top">
				<Box style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{value}</Box>
			</Tooltip>
		)
	}

	const getColumns = () => {
		let columns = []
		columns = [
			{
				field: "name",
				headerName: t("bifrost.table_headers.fileName"),
				sortable: false,
				resizable: true,
				width: bifrostJobDetails?.status === "Partially-successful" ? 430 : 650,
				renderCell: (params) => getToolTipSection(params?.row?.name),
			},
			{
				field: "original_file_type",
				headerName: t("bifrost.table_headers.fileType"),
				sortable: false,
				width: 180,
			},
			{
				field: "size",
				headerName: t("bifrost.table_headers.fileSize"),
				sortable: false,
				width: 180,
			},
			{
				field: "file_status",
				headerName: t("bifrost.table_headers.fileStatus"),
				sortable: false,
				width: bifrostJobDetails?.status === "Partially-successful" ? 110 : 180,
				renderCell: (params) => (
					<Chip
						label={params?.row?.file_status}
						size="small"
						style={{
							color: getStatusColor(params?.row?.file_status, theme).color,
							backgroundColor: getStatusColor(params?.row?.file_status, theme).background,
						}}
					/>
				),
			},
		]

		if (bifrostJobDetails?.status === "Partially-successful") {
			columns.push({
				field: "exception",
				renderHeader: () => <></>,
				sortable: false,
				width: 410,
				renderCell: (params) => getFailedError(params),
			})
		}

		return columns
	}

	const getFailedError = (params) => {
		return params?.row?.exception?.error ? (
			<Box style={{ display: "flex" }}>
				<Tooltip title={params?.row?.exception?.error} placement="top">
					<Box style={{ display: "flex", alignItems: "center" }}>
						<Typography variant="body2" className={classes.errorMessage}>
							{`${t("bifrost.file_status.error")}: ${params?.row?.exception?.error}`}
						</Typography>
					</Box>
				</Tooltip>
				<IconButton
					aria-label="show error traceback"
					className={classes.errorIcon}
					onClick={(e) => showTracebackPopover(e, params?.row?.exception?.traceback)}
				>
					<ErrorOutlineIcon />
				</IconButton>
				<Popover
					className={classes.tracebackPopover}
					open={showTraceBox}
					anchorEl={anchorEl}
					onClose={hideTracebackPopover}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "left",
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "right",
					}}
				>
					<Typography className={classes.popoverContent}>{popoverContent}</Typography>
				</Popover>
			</Box>
		) : (
			<></>
		)
	}

	const showTracebackPopover = (e, traceback) => {
		e.preventDefault()
		setPopoverContent(traceback)
		setAnchorEl(e.currentTarget)
	}

	const hideTracebackPopover = () => {
		setAnchorEl(null)
	}

	const handleFileStatusChange = (event) => {
		setFileStatus(event.target.value)
	}
	const handleFileTypeChange = (event) => {
		setFileType(event.target.value)
	}

	const handlePageNumberChange = (page) => {
		setPageNumber(page)
	}
	const handlePageSizeChange = (pageSize) => {
		setPageSize(pageSize)
	}

	const getFileStatusFilter = () => {
		return (
			<>
				<FormControl fullWidth className={classes.formControl} variant="outlined" size="small">
					<InputLabel id="file_status-select-label">{t("bifrost.filters.fileStatus")}</InputLabel>
					<Tooltip placement="top" arrow title={fileStatus}>
						<Select
							labelId="file-status-select-label"
							id="file-status-select"
							value={fileStatus}
							label={t("bifrost.filters.fileStatus")}
							onChange={handleFileStatusChange}
							MenuProps={MenuProps}
						>
							<MenuItem value="">
								<em>{t("bifrost.file_status.all")}</em>
							</MenuItem>
							{fileStatusList.map((fileStatus) => {
								return (
									<MenuItem key={fileStatus} value={fileStatus}>
										{fileStatus}
									</MenuItem>
								)
							})}
						</Select>
					</Tooltip>
				</FormControl>
			</>
		)
	}

	const getFileTypeFilter = () => {
		return (
			<>
				<FormControl fullWidth className={classes.formControl} variant="outlined" size="small">
					<InputLabel id="file-type-select-label">{t("bifrost.filters.fileType")}</InputLabel>
					<Tooltip placement="top" arrow title={fileType}>
						<Select
							labelId="file-type-select-label"
							id="file-type-select"
							value={fileType}
							label={t("bifrost.filters.fileType")}
							onChange={handleFileTypeChange}
							MenuProps={MenuProps}
						>
							<MenuItem value="">
								<em>{t("bifrost.file_types.all")}</em>
							</MenuItem>
							{fileTypesList.map((fileType) => {
								return (
									<MenuItem key={fileType} value={fileType}>
										{fileType}
									</MenuItem>
								)
							})}
						</Select>
					</Tooltip>
				</FormControl>
			</>
		)
	}

	const getListView = () => {
		return (
			<>
				<Box style={{ display: "flex", justifyContent: "space-between", width: "50%" }}>
					<Typography className={classes.summarTableTitle}>{t("bifrost.fileSummary.allFiles")}</Typography>
				</Box>

				<TableView
					columns={getColumns()}
					rows={getRows()}
					isLoading={isPageLoading}
					pageInfo={{
						page: pageNumber,
						pageSize: pageSize,
						rowsPerPageOptions: [5, 10, 20, 50],
						totalRowCount: bifrostJobDetails?.pagination?.total_count,
						paginationMode: "server",
						handlePageChange: handlePageNumberChange,
						handlePageSizeChange: handlePageSizeChange,
					}}
					sortInfo={{
						sortingMode: "server",
						sortModel: [],
						onSortModelChange: () => {},
					}}
					checkboxSelection={false}
					footerInfo={{
						hideFooter: false,
						hideSelectionCount: true,
						hidePagination: false,
					}}
					topBarInfo={{
						show: true,
						rightSection: (
							<>
								{getFileTypeFilter()} {getFileStatusFilter()}
							</>
						),
					}}
					totalCountText={`${t("tableColumns.totalFiles")}:  ${bifrostJobDetails?.pagination?.total_count || 0}`}
					showDownloadButton={false}
					showColumnsButton={false}
				/>
			</>
		)
	}

	return (
		<div data-cy="job-details" className={classes.jobDetails}>
			<DetailsLayout
				loading={isPageLoading}
				titleProps={{
					title: t("bifrost.titles.jobDetails"),
					name: bifrostJobDetails?.name,
					status: bifrostJobDetails?.status,
				}}
				actionItems={getActionItems()}
				listView={getListView()}
				showActionsView={false}
			>
				<>
					<Grid item xs={9}>
						<LabelValue
							label={t("bifrost.metadataKeys.caseName")}
							value={bifrostJobDetails?.case_name}
							loading={isPageLoading}
						/>
					</Grid>
					<Grid item xs={3}>
						<Grid container item direction="row" xs={12}>
							<Grid item xs={7}>
								<LabelValue
									label={t("bifrost.metadataKeys.jobTriggerDate")}
									value={
										!!bifrostJobDetails?.create_time ? new Date(bifrostJobDetails?.create_time).toLocaleString() : "—"
									}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={5}>
								<LabelValue
									label={t("bifrost.metadataKeys.totalTime")}
									value={
										bifrostJobDetails?.text_extract_time + bifrostJobDetails?.post_extract_time === 0
											? t("bifrost.metadataKeys.calculating")
											: getFormattedTime(bifrostJobDetails?.total_time)
									}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={9}>
						<LabelValue
							label={t("bifrost.metadataKeys.processStatus")}
							value={bifrostJobDetails.status}
							loading={isPageLoading}
						/>
					</Grid>
					<Grid item xs={3}>
						<Grid container item direction="row" xs={12}>
							<Grid item xs={7}>
								<LabelValue
									label={t("bifrost.metadataKeys.jobTriggeredBy")}
									value={bifrostJobDetails.created_by_username}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={10}>
						<Grid container item direction="row" xs={10}>
							<Grid item xs={2}>
								<LabelValue
									label={t("bifrost.metadataKeys.totalFileCount")}
									value={bifrostJobDetails?.execution_status?.total || 0}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={2}>
								<LabelValue
									label={t("bifrost.metadataKeys.inProcessFiles")}
									value={bifrostJobDetails?.execution_status?.summary.In_Progress || 0}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={2}>
								<LabelValue
									label={t("bifrost.metadataKeys.completedFiles")}
									value={bifrostJobDetails?.execution_status?.summary?.Done || 0}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={2}>
								<LabelValue
									label={t("bifrost.metadataKeys.failedFiles")}
									value={bifrostJobDetails?.execution_status?.summary?.Failed || 0}
									loading={isPageLoading}
								/>
							</Grid>
							<Grid item xs={2}>
								<LabelValue
									label={t("bifrost.metadataKeys.skippedFiles")}
									value={bifrostJobDetails?.execution_status?.summary?.Skipped || 0}
									loading={isPageLoading}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={2}></Grid>
				</>
			</DetailsLayout>
		</div>
	)
}

export default WithDrawer(PageGeneratedSummaryDetails)
